export const MAX_ASSET_UPLOAD_FILE_SIZE_BYTES = 1024 * 1024 * 1024;

type SizedFileLike = {
  name: string;
  size: number;
};

export function formatUploadBytes(bytes?: number): string {
  if (!Number.isFinite(bytes) || !bytes || bytes <= 0) return '0 B';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${Math.round(bytes)} B`;
}

export function formatUploadSpeed(bytesPerSecond?: number): string {
  if (!Number.isFinite(bytesPerSecond) || !bytesPerSecond || bytesPerSecond <= 0) return '-';
  return `${formatUploadBytes(bytesPerSecond)}/s`;
}

export function formatUploadEta(seconds?: number): string {
  if (!Number.isFinite(seconds) || seconds === undefined || seconds === null || seconds < 0) return '-';
  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
}

export function splitFilesByUploadSize(files: FileList | File[], maxBytes = MAX_ASSET_UPLOAD_FILE_SIZE_BYTES) {
  const allFiles = Array.from(files);
  return {
    acceptedFiles: allFiles.filter((file) => file.size <= maxBytes),
    rejectedFiles: allFiles.filter((file) => file.size > maxBytes),
  };
}

export function buildUploadTooLargeMessage(
  files: ArrayLike<SizedFileLike>,
  maxBytes = MAX_ASSET_UPLOAD_FILE_SIZE_BYTES,
): string {
  const oversizedFiles = Array.from(files);
  if (oversizedFiles.length === 0) {
    return `Filen är för stor. Max tillåten filstorlek är ${formatUploadBytes(maxBytes)}.`;
  }

  const listedFiles = oversizedFiles
    .slice(0, 3)
    .map((file) => `${file.name} (${formatUploadBytes(file.size)})`)
    .join(', ');
  const suffix = oversizedFiles.length > 3 ? ` + ${oversizedFiles.length - 3} till` : '';
  return `Filen är för stor. Max tillåten filstorlek är ${formatUploadBytes(maxBytes)}. Över gränsen: ${listedFiles}${suffix}.`;
}

export function getUploadErrorMessage(error: any): string {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const backendMessage = error?.response?.data?.message;

  if (status === 413 || code === 'UPLOAD_FILE_TOO_LARGE') {
    return backendMessage || `Filen är för stor. Max tillåten filstorlek är ${formatUploadBytes(MAX_ASSET_UPLOAD_FILE_SIZE_BYTES)}.`;
  }

  if (status === 401 && typeof code === 'string' && code.startsWith('AUTH_')) {
    return 'Din session har gått ut eller är ogiltig. Logga in igen och försök på nytt.';
  }

  if (typeof backendMessage === 'string' && backendMessage.includes('File type not allowed')) {
    return 'Formatet stöds inte för uppladdning. Tillåtna format: JPG, PNG, WEBP, GIF, TIFF, SVG, PDF och videoformat som MP4/MOV/AVI.';
  }

  if (backendMessage) return backendMessage;

  if (error?.code === 'ERR_NETWORK') {
    return 'Nätverksfel eller avbruten anslutning under uppladdningen.';
  }

  if (error?.message) return error.message;

  return 'Uppladdningen misslyckades.';
}
